import React, { useEffect, useState, useCallback } from "react";
import { api, fmtDate, formatError, TIPOS_COMBUSTIVEL } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Fuel, CornerDownLeft, Trash2, BarChart3, Filter } from "lucide-react";

const empty = {
  vehicle_id: "",
  veiculo: "",
  placa: "",
  motorista_nome: "",
  destino: "",
  projeto_atividade: "",
  km_inicial: 0,
  quantidade_litros: 0,
  tipo_combustivel: "Diesel",
  responsavel_abastecimento: "",
  observacoes: "",
  data_saida: "",
};

const emptyRet = { data_retorno: "", km_final: 0, observacao_retorno: "" };

const statusBadge = (s) => {
  const map = {
    aberto: { v: "warn", t: "Em rota" },
    retornado: { v: "success", t: "Retornado" },
    cancelado: { v: "default", t: "Cancelado" },
  };
  const c = map[s] || map.aberto;
  return <Badge variant={c.v}>{c.t}</Badge>;
};

export default function FuelOuts() {
  const { user, hasRole } = useAuth();
  const canEdit = hasRole("admin", "almoxarife", "tesoureiro", "presidencia");
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [retOpen, setRetOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [retForm, setRetForm] = useState(emptyRet);
  const [retTarget, setRetTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [placaFilter, setPlacaFilter] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [vehicleReport, setVehicleReport] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (placaFilter.trim()) params.placa = placaFilter.trim();
      const { data } = await api.get("/fuel-outs", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, placaFilter]);

  const loadVehicles = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles");
      setVehicles(data.filter((v) => v.ativo !== false));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadVehicles(); }, [loadVehicles]);

  const openCreate = () => {
    setForm({ ...empty, responsavel_abastecimento: user?.name || "" });
    setOpen(true);
  };

  const onVehicleChange = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    if (!v) { setForm((f) => ({ ...f, vehicle_id: "" })); return; }
    setForm((f) => ({
      ...f,
      vehicle_id: v.id,
      veiculo: `${v.marca || ""} ${v.modelo || ""}`.trim(),
      placa: v.placa || "",
      tipo_combustivel: v.combustivel_padrao || f.tipo_combustivel,
      km_inicial: v.hodometro || f.km_inicial,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        km_inicial: parseFloat(form.km_inicial) || 0,
        quantidade_litros: parseFloat(form.quantidade_litros) || 0,
      };
      if (!payload.motorista_nome.trim()) { toast.error("Informe o motorista/operador"); return; }
      if (!(payload.quantidade_litros > 0)) { toast.error("Litros deve ser maior que zero"); return; }
      await api.post("/fuel-outs", payload);
      toast.success("Saída de combustível registrada");
      setOpen(false); setForm(empty); load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openReturn = (it) => {
    setRetTarget(it);
    setRetForm({ ...emptyRet, km_final: it.km_inicial || 0 });
    setRetOpen(true);
  };

  const submitReturn = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        data_retorno: retForm.data_retorno || undefined,
        km_final: parseFloat(retForm.km_final) || 0,
        observacao_retorno: retForm.observacao_retorno,
      };
      if (payload.km_final < (retTarget.km_inicial || 0)) {
        toast.error("Km final não pode ser menor que o Km inicial");
        return;
      }
      await api.patch(`/fuel-outs/${retTarget.id}/return`, payload);
      toast.success("Retorno registrado · Km/L calculado");
      setRetOpen(false); setRetTarget(null); setRetForm(emptyRet); load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir esta saída de combustível?")) return;
    try {
      await api.delete(`/fuel-outs/${id}`);
      toast.success("Excluída");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const loadReport = async () => {
    try {
      const { data } = await api.get("/fuel-outs/report/by-vehicle");
      setVehicleReport(data);
      setShowReport(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="fuel-outs-page">
      <SectionTitle
        action={
          <div className="flex gap-2">
            <Btn variant="dark" onClick={loadReport} data-testid="fuel-out-report-btn"><BarChart3 className="w-4 h-4" />Histórico por Veículo</Btn>
            {canEdit && (
              <Btn onClick={openCreate} data-testid="add-fuel-out-btn"><Plus className="w-4 h-4" />Nova Saída</Btn>
            )}
          </div>
        }
      >
        Saída de Combustível · <Fuel className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      {/* Filtros */}
      <div className="brutal-card-sm flex flex-wrap items-center gap-3 px-3 py-2 mb-4 bg-white">
        <Filter className="w-4 h-4 text-[#555]" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border-2 border-[#111] px-2 py-1 text-sm bg-white font-mono-data"
          data-testid="fuel-out-status-filter"
        >
          <option value="">Todos os status</option>
          <option value="aberto">Em rota (aberto)</option>
          <option value="retornado">Retornado</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <input
          value={placaFilter}
          onChange={(e) => setPlacaFilter(e.target.value)}
          placeholder="Filtrar por placa..."
          className="border-2 border-[#111] px-2 py-1 text-sm bg-white font-mono-data outline-none"
          data-testid="fuel-out-placa-filter"
        />
      </div>

      {/* Tabela */}
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead>
            <tr>
              <th>Protocolo</th>
              <th>Data</th>
              <th>Veículo</th>
              <th>Motorista</th>
              <th>Destino</th>
              <th>Km Ini</th>
              <th>Km Fim</th>
              <th>Km Rodado</th>
              <th>Litros</th>
              <th>Km/L</th>
              <th>Status</th>
              {canEdit && <th className="w-32">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canEdit ? 12 : 11} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && items.length === 0 && <EmptyState message="Nenhuma saída de combustível registrada." cols={canEdit ? 12 : 11} />}
            {items.map((it) => (
              <tr key={it.id} data-testid={`fuel-out-row-${it.id}`}>
                <td className="font-mono-data text-xs font-bold">{it.protocolo}</td>
                <td className="font-mono-data text-xs">{fmtDate(it.data_saida)}</td>
                <td>
                  <div className="font-medium">{it.veiculo || "—"}</div>
                  <div className="font-mono-data text-xs text-[#555]">{it.placa || "—"}</div>
                </td>
                <td className="text-sm">{it.motorista_nome}</td>
                <td className="text-sm">{it.destino || "—"}</td>
                <td className="font-mono-data text-xs">{it.km_inicial?.toLocaleString("pt-BR") || "—"}</td>
                <td className="font-mono-data text-xs">{it.km_final != null ? it.km_final.toLocaleString("pt-BR") : "—"}</td>
                <td className="font-mono-data text-xs font-bold">{it.km_rodado?.toLocaleString("pt-BR") || "—"}</td>
                <td className="font-mono-data text-xs">{it.quantidade_litros}</td>
                <td className="font-mono-data text-xs font-bold text-[#3D6B57]">{it.consumo_medio_km_l ? `${it.consumo_medio_km_l} km/L` : "—"}</td>
                <td>{statusBadge(it.status)}</td>
                {canEdit && (
                  <td>
                    <div className="flex gap-1">
                      {it.status === "aberto" && (
                        <button
                          onClick={() => openReturn(it)}
                          data-testid={`return-fuel-out-${it.id}`}
                          title="Registrar retorno"
                          className="p-1.5 hover:bg-[#1A7F37] hover:text-white border border-[#111]"
                        >
                          <CornerDownLeft className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`delete-fuel-out-${it.id}`}
                        className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Nova Saída */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nova Saída de Combustível" size="xl">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="fuel-out-form">
          <Field label="Data/Hora da Saída" type="datetime-local" value={form.data_saida} onChange={(v) => setForm({ ...form, data_saida: v })} />
          <SelectField
            label="Veículo / Máquina (cadastrado)"
            value={form.vehicle_id}
            onChange={onVehicleChange}
            options={[{ value: "", label: "— informar manualmente —" }, ...vehicles.map((v) => ({ value: v.id, label: `${v.placa} · ${v.marca || ""} ${v.modelo || ""}` }))]}
          />
          <Field label="Veículo (descrição)" value={form.veiculo} onChange={(v) => setForm({ ...form, veiculo: v })} placeholder="Ex.: Mitsubishi L200" />
          <Field label="Placa" value={form.placa} onChange={(v) => setForm({ ...form, placa: (v || "").toUpperCase() })} placeholder="ABC-1D23" />
          <Field label="Motorista / Operador *" required value={form.motorista_nome} onChange={(v) => setForm({ ...form, motorista_nome: v })} />
          <Field label="Destino" value={form.destino} onChange={(v) => setForm({ ...form, destino: v })} placeholder="Ex.: Rio Cuparí" />
          <Field label="Projeto / Atividade" value={form.projeto_atividade} onChange={(v) => setForm({ ...form, projeto_atividade: v })} colspan />
          <Field label="Km Inicial *" type="number" required value={form.km_inicial} onChange={(v) => setForm({ ...form, km_inicial: v })} testid="fuel-out-km-inicial" />
          <Field label="Quantidade (litros) *" type="number" required value={form.quantidade_litros} onChange={(v) => setForm({ ...form, quantidade_litros: v })} testid="fuel-out-litros" />
          <SelectField label="Tipo de Combustível" value={form.tipo_combustivel} onChange={(v) => setForm({ ...form, tipo_combustivel: v })} options={(TIPOS_COMBUSTIVEL || ["Diesel", "Gasolina", "Etanol"]).map((t) => ({ value: t, label: t }))} />
          <Field label="Responsável pelo Abastecimento" value={form.responsavel_abastecimento} onChange={(v) => setForm({ ...form, responsavel_abastecimento: v })} />
          <TextArea label="Observação" value={form.observacoes} onChange={(v) => setForm({ ...form, observacoes: v })} />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-fuel-out-btn">Registrar Saída</Btn>
          </div>
        </form>
      </Modal>

      {/* Modal Retorno */}
      <Modal open={retOpen} onClose={() => setRetOpen(false)} title={`Retorno · ${retTarget?.protocolo || ""}`} size="md">
        {retTarget && (
          <form onSubmit={submitReturn} className="grid grid-cols-2 gap-4" data-testid="fuel-out-return-form">
            <div className="col-span-2 brutal-card-sm p-3 bg-[#F0F7F2]">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Saída original</div>
              <div className="text-sm">
                {retTarget.veiculo} · {retTarget.placa} · Km inicial: <strong>{retTarget.km_inicial?.toLocaleString("pt-BR")}</strong> · Litros: <strong>{retTarget.quantidade_litros}</strong>
              </div>
            </div>
            <Field label="Data/Hora do Retorno" type="datetime-local" value={retForm.data_retorno} onChange={(v) => setRetForm({ ...retForm, data_retorno: v })} />
            <Field label="Km Final *" type="number" required value={retForm.km_final} onChange={(v) => setRetForm({ ...retForm, km_final: v })} testid="fuel-out-km-final" />
            <TextArea label="Observação do Retorno" value={retForm.observacao_retorno} onChange={(v) => setRetForm({ ...retForm, observacao_retorno: v })} />
            <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
              <Btn type="button" variant="secondary" onClick={() => setRetOpen(false)}>Cancelar</Btn>
              <Btn type="submit" variant="success" data-testid="save-fuel-out-return-btn">Confirmar Retorno</Btn>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal Histórico por veículo */}
      <Modal open={showReport} onClose={() => setShowReport(false)} title="Histórico por Veículo" size="xl">
        <div className="overflow-x-auto" data-testid="fuel-out-report-modal">
          <table className="brutal-table">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Veículo</th>
                <th>Viagens</th>
                <th>Km Total</th>
                <th>Litros Total</th>
                <th>Média Km/L</th>
              </tr>
            </thead>
            <tbody>
              {vehicleReport.length === 0 && <EmptyState message="Sem retornos registrados ainda." cols={6} />}
              {vehicleReport.map((r, i) => (
                <tr key={`${r.placa}-${i}`}>
                  <td className="font-mono-data text-xs font-bold">{r.placa}</td>
                  <td>{r.veiculo || "—"}</td>
                  <td className="font-mono-data text-xs">{r.viagens}</td>
                  <td className="font-mono-data text-xs">{r.km_total?.toLocaleString("pt-BR")}</td>
                  <td className="font-mono-data text-xs">{r.litros_total}</td>
                  <td className="font-mono-data text-xs font-bold text-[#3D6B57]">{r.media_km_l} km/L</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}
